import { Route, Routes } from 'react-router';
import { Trans } from '@lingui/react/macro';

import { EmptyState, IllustrationNoResults } from '@/components/EmptyState';
import { TableQueryWrapper } from '@/components/TableQueryWrapper/TableQueryWrapper';
import Badge from '@/design_system/Badge/Badge';
import { PaginationFooter } from '@/design_system/Pagination/Pagination';
import Stack from '@/design_system/Stack';
import { Body, Cell, Column, Header, Row, Table } from '@/design_system/Table/Table';
import Paragraph from '@/design_system/Typography/Paragraph';
import { Entity, useEntities } from '@/models/entity';

import {
  AfterSalesTraitIcon,
  StockLocationTraitIcon,
  StoreTraitIcon,
  WorkshopTraitIcon,
} from './components/TraitIcon';
import { Entity as EntityPage } from './Entity/Entity';
import { Filters } from './Filters/Filters';
import { FiltersStateContext, useFiltersState } from './Filters/FiltersState';

const ENTITIES_PER_PAGE = 20;

export const EntitiesRoutes = () => {
  return (
    <Routes>
      <Route path="" element={<Entities />} />
      <Route path="/:id/*" element={<EntityPage />} />
    </Routes>
  );
};

const Entities = () => {
  const filters = useFiltersState();

  const {
    data: { entities, meta } = { entities: [] },
    isLoading: isEntitiesLoading,
    isPlaceholderData: isEntitiesPlaceholder,
    isError: isEntitiesError,
  } = useEntities(
    {
      limit: ENTITIES_PER_PAGE,
      offset: (filters.page.page - 1) * ENTITIES_PER_PAGE,
      search: filters.search.debouncedSearch,
      traits: filters.traits.traits,
      external:
        filters.types.types.length === 1 ? filters.types.types[0] === 'external' : undefined,
      groups: filters.groups.groups.map(({ id }) => id),
    },
    {
      keepPreviousData: true,
    }
  );

  return (
    <main style={{ gap: '1rem' }}>
      <FiltersStateContext value={filters}>
        <Filters />
      </FiltersStateContext>
      <TableQueryWrapper
        isLoading={isEntitiesLoading || isEntitiesPlaceholder}
        isError={isEntitiesError}
      >
        {entities && entities.length === 0 ? (
          <EmptyState
            subtitle={
              <Stack>
                <Paragraph>
                  <Trans id="settings.organization.entities.entities.no-results.1">
                    No results are matching your criteria.
                  </Trans>
                </Paragraph>
                <Paragraph>
                  <Trans id="settings.organization.entities.entities.no-results.2">
                    Please try with different keywords or filters.
                  </Trans>
                </Paragraph>
              </Stack>
            }
            illustration={<IllustrationNoResults />}
          />
        ) : (
          <Table columnWidths={['auto', '1fr', '1fr']} bordered>
            <Header>
              <Row>
                <Column>
                  <Trans id="settings.organization.entities.entities.table.header.trait">
                    Trait
                  </Trans>
                </Column>
                <Column>
                  <Trans id="settings.organization.entities.entities.table.header.name">Name</Trans>
                </Column>
                <Column>
                  <Trans id="settings.organization.entities.entities.table.header.groups">
                    Groups
                  </Trans>
                </Column>
              </Row>
            </Header>

            <Body>
              {entities?.map((entity) => (
                <EntityRow key={entity.id} entity={entity} />
              ))}
            </Body>

            <PaginationFooter
              itemsPerPage={ENTITIES_PER_PAGE}
              page={filters.page.page}
              onPageChange={filters.page.setPage}
              count={meta?.count}
            />
          </Table>
        )}
      </TableQueryWrapper>
    </main>
  );
};

const EntityRow = ({ entity }: { entity: Entity }) => {
  const isStore = entity.entityOrganizations.some((eo) => eo.trait === 'store');
  const isAfterSales = entity.entityOrganizations.some((eo) => eo.trait === 'aftersales');
  const isWorkshop = entity.entityOrganizations.some((eo) => eo.trait === 'workshop');
  const isStockLocation = entity.entityOrganizations.some((eo) => eo.trait === 'stockLocation');
  const isExternal = entity.entityOrganizations.some((eo) => eo.external);

  return (
    <Row to={`/settings_beta/network/entities/${entity.id}`}>
      <Cell>
        <Stack row gap="0.25rem" style={{ fontSize: '1rem' }} className="text-secondary">
          {isStore && <StoreTraitIcon />}
          {isAfterSales && <AfterSalesTraitIcon />}
          {isWorkshop && <WorkshopTraitIcon />}
          {isStockLocation && <StockLocationTraitIcon />}
        </Stack>
      </Cell>
      <Cell isLink>
        <Stack row gap="0.5rem" alignItems="center">
          <Paragraph weight="medium">{entity.name}</Paragraph>
          {isExternal && (
            <Badge size="x-small" color="secondary">
              <Trans id="settings.organization.entities.entities.external">External</Trans>
            </Badge>
          )}
        </Stack>
      </Cell>
      <Cell>
        <Paragraph size="200">
          {entity.groups.map((group) => group.name).join(', ') || '-'}
        </Paragraph>
      </Cell>
    </Row>
  );
};
